import { useRef, useState } from "react";
import { Lock, FileText, Zap, ShieldCheck, RotateCcw, Download } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ConfirmResetDialog } from "@/components/ConfirmResetDialog";

// ============================================================================
// W-2 Ingest Portal · Premium Tax Shield exclusive.
// Space-age cockpit: pulsing cyan/violet halo, white-hot laser sweep on upload,
// gold + emerald confetti burst on success, then a "Generate IRS Form 1040 +
// Schedule C Packet" button that drops a bank-grade PDF client-side.
// ============================================================================
const fmt = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// ============================================================================
// Intelligent Odometer Engines — powering the upgraded Schedule-C packet.
// All figures use widely-published 2026 IRS inflation-adjusted values (single
// filer). Change these constants at year-end when the Rev. Proc. drops.
// ============================================================================

// 2026 single-filer standard deduction (Rev. Proc. 2025 projection).
const STD_DEDUCTION_2026 = 15_000;

// 2026 Married-Filing-Jointly standard deduction (Rev. Proc. 2025 projection).
const STD_DEDUCTION_2026_MFJ = 30_000;

// 2026 single-filer ordinary income tax brackets — [ceiling, rate].
// Tail rate (37%) applies to any income above the last ceiling.
const BRACKETS_2026 = [
  [11_925,  0.10],
  [48_475,  0.12],
  [103_350, 0.22],
  [197_300, 0.24],
  [250_525, 0.32],
  [626_350, 0.35],
  [Infinity, 0.37],
];

// 2026 Married-Filing-Jointly brackets (widened to ~2× the single ceilings).
const BRACKETS_2026_MFJ = [
  [23_850,  0.10],
  [96_950,  0.12],
  [206_700, 0.22],
  [394_600, 0.24],
  [501_050, 0.32],
  [751_600, 0.35],
  [Infinity, 0.37],
];

// 2026 wealth-shield contribution ceilings.
const SOLO_401K_2026 = 23_500;
const HSA_SELF_2026  =  4_400;

function projectYearEndBallot(box1, box2, filingStatus = "single") {
  // Standard-deduction path. Returns computed federal ordinary tax liability
  // + delta vs Box-2 withholding. Positive delta → projected refund;
  // negative → additional liability due. Cent-precise (no dollar rounding).
  const wages = Number(box1) || 0;
  const withheld = Number(box2) || 0;
  const isMFJ = filingStatus === "mfj";
  const stdDeduction = isMFJ ? STD_DEDUCTION_2026_MFJ : STD_DEDUCTION_2026;
  const brackets = isMFJ ? BRACKETS_2026_MFJ : BRACKETS_2026;
  const taxable = Math.max(0, wages - stdDeduction);
  let tax = 0;
  let last = 0;
  for (const [ceil, rate] of brackets) {
    if (taxable <= last) break;
    const chunk = Math.min(taxable, ceil) - last;
    tax += chunk * rate;
    last = ceil;
  }
  // Round to nearest cent — preserves the $XXX.XX precision the packet needs.
  tax = Math.round(tax * 100) / 100;
  const delta = Math.round((withheld - tax) * 100) / 100;
  const marginalRate = (() => {
    for (const [ceil, rate] of brackets) {
      if (taxable <= ceil) return rate;
    }
    return 0.37;
  })();
  return {
    wages,
    withheld,
    taxable,
    tax,
    delta,
    isRefund: delta >= 0,
    marginalRate,
    filingStatus,
    stdDeduction,
  };
}

// Cent-precise money formatter for the packet — always two decimals.
const fmt2 = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ingestionAccuracyScore(w2 = {}) {
  // Field-density heuristic across the 8 canonical W-2 signals the Vision
  // pipeline should extract. Ceiling is 98.4% (Vision never legitimately
  // claims 100% confidence — retains a 1.6% OCR-noise reserve). Floor is
  // 62% so a barely-parsed W-2 still surfaces the two must-have wage boxes.
  const signals = [
    w2.gross_wages ?? w2.wages_tips,
    w2.federal_withheld,
    w2.employer,
    w2.employer_ein,
    w2.state_code || w2.state,
    w2.state_wages,
    w2.state_withheld,
    w2.tax_year,
  ];
  const filled = signals.filter((v) => v !== null && v !== undefined && v !== "" && v !== 0).length;
  const raw = (filled / signals.length) * 98.4;
  const pct = Math.max(62.0, Math.round(raw * 10) / 10);
  const tier =
    pct >= 95 ? "High-Confidence Shield Verified" :
    pct >= 85 ? "Verified" :
    pct >= 70 ? "Partial Verification" :
                "Low Confidence · Review Recommended";
  return { pct, tier, label: `${pct.toFixed(1)}% [${tier}]` };
}

function wealthShieldBlueprint(box1, ballot) {
  // Deterministic action-item paragraph. For LIABILITY cases we compute the
  // exact pre-tax sweep across HSA + Solo 401(k) that would zero out the
  // projected liability at the user's marginal rate. For REFUND cases we
  // surface the max headroom still available so they can grow the refund.
  const wages = Number(box1) || 0;
  const marginalPct = Math.round(ballot.marginalRate * 100);
  const soloCeiling = SOLO_401K_2026;
  const hsaCeiling  = HSA_SELF_2026;

  if (!ballot.isRefund) {
    // Liability path — solve for the sweep amount needed to wipe out the delta.
    const liability = Math.abs(ballot.delta);
    const sweepNeeded = Math.round((liability / ballot.marginalRate) * 100) / 100;
    // Deploy HSA first (fewest strings), then Solo 401(k), then note the remainder.
    const hsaSweep  = Math.min(hsaCeiling, sweepNeeded);
    const soloSweep = Math.min(soloCeiling, Math.max(0, sweepNeeded - hsaSweep));
    const totalSweep = Math.round((hsaSweep + soloSweep) * 100) / 100;
    const stillShort = Math.max(0, Math.round((sweepNeeded - totalSweep) * 100) / 100);
    const projectedTaxCut = Math.round(totalSweep * ballot.marginalRate * 100) / 100;
    const netLiability = Math.max(0, Math.round((liability - projectedTaxCut) * 100) / 100);
    return {
      liability,
      sweepNeeded,
      hsaSweep,
      soloSweep,
      totalSweep,
      stillShort,
      projectedTaxCut,
      netLiability,
      soloShield: soloSweep,
      hsaShield: hsaSweep,
      totalShield: totalSweep,
      marginalPct,
      paragraph:
        `Wealth-Shield Blueprint · At your ${marginalPct}% marginal bracket, sweeping ` +
        `${fmt2(hsaSweep)} into your HSA and ${fmt2(soloSweep)} into your Solo 401(k) ` +
        `(combined ${fmt2(totalSweep)}) delivers a ${fmt2(projectedTaxCut)} tax cut — ` +
        (stillShort > 0
          ? `which drops your projected liability from ${fmt2(liability)} down to ${fmt2(netLiability)}. ` +
            `To fully zero it out you'd need another ${fmt2(stillShort)} of pre-tax sweep capacity beyond the current 2026 ceilings.`
          : `which drives your projected liability from ${fmt2(liability)} down to exactly $0.00 before filing.`) +
        ` Statute stack: IRC §223 (HSA) + §402(g) (401(k) elective). Solo 401(k) requires 1099/self-employment income to open — confirm plan eligibility with your CPA.`,
    };
  }

  // Refund path — max headroom still open (worst-case, no prior contributions).
  const soloShield = Math.min(soloCeiling, Math.round(wages * 0.25));
  const hsaShield  = hsaCeiling;
  const totalShield = soloShield + hsaShield;
  const projectedTaxCut = Math.round(totalShield * ballot.marginalRate * 100) / 100;
  return {
    soloShield,
    hsaShield,
    totalShield,
    projectedTaxCut,
    marginalPct,
    paragraph:
      `Wealth-Shield Blueprint · Even with a projected refund, at your ${marginalPct}% marginal bracket you still have ` +
      `${fmt2(soloShield)} of Solo 401(k) headroom and ${fmt2(hsaShield)} of HSA self-only headroom for 2026 — ` +
      `a combined ${fmt2(totalShield)} of pre-tax capacity. Deploying the full stack pushes an estimated ` +
      `${fmt2(projectedTaxCut)} back into your pocket and materially grows the ballot below. ` +
      `Statute stack: IRC §223 (HSA) + §402(g) (401(k) elective). Solo 401(k) requires 1099/self-employment income to open.`,
  };
}

function fireConfettiBurst() {
  // Lightweight DOM-based particle burst — no external lib.  Emits 40 gold +
  // emerald squares from screen center that fall and fade.
  if (typeof document === "undefined") return;
  const root = document.createElement("div");
  root.setAttribute("data-testid", "w2-confetti-root");
  root.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9990;overflow:hidden;";
  const colors = ["#fde047", "#facc15", "#10b981", "#34d399", "#67e8f9"];
  const rand = (a, b) => a + Math.random() * (b - a);
  for (let i = 0; i < 40; i++) {
    const p = document.createElement("span");
    const size = rand(6, 12);
    p.style.cssText = `position:absolute;left:${rand(20, 80)}%;top:${rand(30, 55)}%;width:${size}px;height:${size}px;background:${colors[i % colors.length]};border-radius:2px;transform:rotate(${rand(0, 360)}deg);opacity:0.95;box-shadow:0 0 8px ${colors[i % colors.length]};transition:transform 1400ms cubic-bezier(.22,1,.36,1),opacity 1400ms ease-out;`;
    root.appendChild(p);
    requestAnimationFrame(() => {
      p.style.transform = `translate(${rand(-260, 260)}px, ${rand(220, 460)}px) rotate(${rand(180, 720)}deg)`;
      p.style.opacity = "0";
    });
  }
  document.body.appendChild(root);
  setTimeout(() => root.remove(), 1600);
}

export function W2IngestPortal() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [w2, setW2] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [filingStatus, setFilingStatus] = useState("single"); // "single" | "mfj"

  // ---- Taxpayer identification (persisted locally so a returning user doesn't
  // retype for every packet). Never sent to the backend — packet is generated
  // entirely client-side, so SSNs stay on the user's device. ----
  const [taxpayer, setTaxpayer] = useState(() => {
    try {
      const raw = window.localStorage.getItem("rs.taxpayer.v1");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [taxpayerOpen, setTaxpayerOpen] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const sigCanvasRef = useRef(null);
  const sigDrawing = useRef(false);
  const sigLast = useRef(null);
  // Tax Packet Reset Switch — dual-action wipe (local state + backend W-2 rows).
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [schedCLoading, setSchedCLoading] = useState(false);
  const patchTaxpayer = (k, v) => setTaxpayer((prev) => {
    const next = { ...prev, [k]: v };
    try { window.localStorage.setItem("rs.taxpayer.v1", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  const performTaxPacketReset = async () => {
    if (resetBusy) return;
    setResetBusy(true);
    const toastId = toast.loading("♻️ Wiping tax packet · flushing W-2 vault…");
    try {
      // Backend: purge all W-2 documents for this user.
      await api.delete("/v1/ingest/w2-documents").catch(() => null);
      // Frontend: flush every local hook & DOM cache back to baseline.
      setW2(null);
      setPreview(null);
      setSweeping(false);
      setBusy(false);
      setFilingStatus("single");
      setTaxpayer({});
      setSignatureDataUrl(null);
      setTaxpayerOpen(false);
      const c = sigCanvasRef.current;
      if (c) { const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height); }
      try {
        window.localStorage.removeItem("rs.taxpayer.v1");
        window.localStorage.removeItem("rs.vault.taxYear");
      } catch { /* ignore */ }
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Tax packet cleared · form back to baseline", { id: toastId });
      window.dispatchEvent(new CustomEvent("reset-audit-log-updated"));
      setResetOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed", { id: toastId });
    } finally {
      setResetBusy(false);
    }
  };

  const onFile = async (raw) => {
    if (!raw || busy) return;
    // Local preview + sweep animation
    setPreview(URL.createObjectURL(raw));
    setSweeping(true);
    setBusy(true);
    const toastId = toast.loading("Ingesting W-2 · OpenAI Vision chroma-extracting…");
    try {
      const form = new FormData();
      form.append("file", raw);
      const { data } = await api.post("/v1/ingest/w2-document", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setW2(data?.w2 || null);
      toast.success(
        `W-2 ingested · Box 1 ${fmt(data?.w2?.gross_wages ?? data?.w2?.wages_tips)} · Box 2 ${fmt(data?.w2?.federal_withheld)}`,
        { id: toastId },
      );
      // Dispatch success event so PaycheckOptimizerNode can blend W-2 wages
      // into its trajectory chart on the next refresh.
      window.dispatchEvent(new CustomEvent("w2-document-ingested", { detail: data.w2 }));
      fireConfettiBurst();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "W-2 ingestion failed", { id: toastId });
    } finally {
      setBusy(false);
      // let the sweep finish its cycle
      setTimeout(() => setSweeping(false), 1500);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const generatePacket = async () => {
    if (!w2) { toast.error("Ingest a W-2 first"); return; }
    try {
      const box1 = Number(w2.gross_wages ?? w2.wages_tips ?? 0);
      const box2 = Number(w2.federal_withheld ?? 0);
      const ballot   = projectYearEndBallot(box1, box2, filingStatus);
      const accuracy = ingestionAccuracyScore(w2);
      const shield   = wealthShieldBlueprint(box1, ballot);

      // Pull Cash Flash totals so Schedule C Part I gets hydrated.
      let cashFlashGross = 0;
      let cashFlashSE = 0;
      let cashFlashCount = 0;
      try {
        const cf = await api.get("/v1/ingest/cash-flash?limit=500");
        const rows = cf?.data?.entries || [];
        cashFlashCount = rows.length;
        cashFlashGross = rows.reduce((s, r) => s + Number(r.cash_amount_pennies || 0), 0) / 100;
        cashFlashSE    = rows.reduce((s, r) => s + Number(r.system_calculated_tax?.self_employment_tax_cents || 0), 0) / 100;
      } catch { /* non-blocking */ }

      // Schedule C Part II auto-pull — Auto Vault × business-use%, Home Vault
      // utility deductions, and Capital Vault Year-1 §179/MACRS. The Multi-Year
      // Slice slider (SpecializedVault) writes the active year into localStorage
      // as `rs.vault.taxYear`; the packet honors that so Line 9/13/25 pull ONLY
      // from the currently-selected fiscal slice.
      let autoLine9 = 0, homeLine25 = 0, capitalLine13 = 0;
      let autoCount = 0, homeCount = 0, capitalCount = 0;
      let packetTaxYear = new Date().getFullYear();
      try {
        const stored = Number(localStorage.getItem("rs.vault.taxYear"));
        if ([2024, 2025, 2026].includes(stored)) packetTaxYear = stored;
      } catch { /* non-blocking */ }
      const inSlice = (d) => {
        const s = String(d?.date || d?.uploaded_at || "");
        return s.startsWith(`${packetTaxYear}-`) || s.startsWith(`${packetTaxYear}/`);
      };
      try {
        const [autoR, homeR, capR, mileR] = await Promise.all([
          api.get("/v1/ingest/vault/auto?limit=200").catch(() => null),
          api.get("/v1/ingest/vault/home?limit=200").catch(() => null),
          api.get("/v1/ingest/vault/capital?limit=200").catch(() => null),
          api.get("/mileage").catch(() => null),
        ]);
        const bizUse = (() => {
          const m = mileR?.data;
          const total = Number(m?.total_miles || 0);
          const biz = Number(m?.business_miles || 0);
          return total > 0 ? Math.min(1, biz / total) : 1;
        })();
        const autoRowsAll = autoR?.data?.docs || autoR?.data?.documents || [];
        const homeRowsAll = homeR?.data?.docs || homeR?.data?.documents || [];
        const capRowsAll  = capR?.data?.docs  || capR?.data?.documents  || [];
        const autoRows = autoRowsAll.filter(inSlice);
        const homeRows = homeRowsAll.filter(inSlice);
        const capRows  = capRowsAll.filter(inSlice);
        autoCount = autoRows.length;
        homeCount = homeRows.length;
        capitalCount = capRows.length;
        autoLine9 = autoRows.reduce((s, d) => s + (Number(d.total || 0) * bizUse), 0);
        homeLine25 = homeRows.reduce((s, d) => s + Number(d.deductible_amount || 0), 0);
        capitalLine13 = capRows.reduce((s, d) => {
          // §179 election forces the FULL purchase price into current year
          // Line 13 instead of the Year-1 MACRS number.
          if (d.section_179_elected) return s + Number(d.purchase_price || 0);
          return s + Number((d.depreciation_schedule && d.depreciation_schedule[0]?.deduction) || 0);
        }, 0);
      } catch { /* non-blocking */ }
      autoLine9    = Math.round(autoLine9 * 100) / 100;
      homeLine25   = Math.round(homeLine25 * 100) / 100;
      capitalLine13 = Math.round(capitalLine13 * 100) / 100;
      const scheduleCExpenses = autoLine9 + homeLine25 + capitalLine13;
      const scheduleCNet = Math.max(0, cashFlashGross - scheduleCExpenses);
      const totalTaxWithSE = Math.round((ballot.tax + cashFlashSE) * 100) / 100;
      const deltaWithSE = Math.round((ballot.withheld - totalTaxWithSE) * 100) / 100;

      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 48;
      const now = new Date();

      // ============================ PAGE 1 · EXECUTIVE SUMMARY ============================
      doc.setFillColor(15, 23, 42); doc.rect(0, 0, W, 88, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold"); doc.setFontSize(20);
      doc.text("Page 1 · Executive Tax Optimization Strategy", M, 46);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      doc.setTextColor(103, 232, 249);
      doc.text(`Path A · Safe Handoff Builder · Compiled ${now.toLocaleDateString()} · TY ${packetTaxYear} · ${ballot.filingStatus === "mfj" ? "Married Filing Jointly" : "Single Filer"}`, M, 68);

      const chipW = 210;
      const chipX = W - M - chipW;
      doc.setFillColor(16, 185, 129);
      doc.roundedRect(chipX, 18, chipW, 52, 8, 8, "F");
      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold"); doc.setFontSize(8);
      doc.text("SYSTEM DATA INGESTION ACCURACY SCORE", chipX + 10, 34);
      doc.setFontSize(18);
      doc.text(`${accuracy.pct.toFixed(1)}%`, chipX + 10, 55);
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.2);
      doc.text(`[${accuracy.tier}]`, chipX + chipW - 10, 55, { align: "right" });
      doc.setTextColor(0);

      let y = 120;
      doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text("Form 1040 · Salary Section", M, y); y += 6;
      doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 8;
      autoTable(doc, {
        startY: y,
        head: [["Line", "Description", "Amount"]],
        body: [
          ["1a", "Total W-2 wages (Box 1 · Wages, tips, other compensation)", fmt2(w2.gross_wages ?? w2.wages_tips)],
          ["25a", "Federal income tax withheld from W-2 (Box 2)", fmt2(w2.federal_withheld)],
          ["State", `${w2.state_code || w2.state || "—"} · State wages (Box 16)`, fmt2(w2.state_wages || 0)],
          ["State", `${w2.state_code || w2.state || "—"} · State tax withheld (Box 17)`, fmt2(w2.state_withheld || 0)],
        ],
        theme: "grid",
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: "bold" },
        columnStyles: { 2: { halign: "right", fontStyle: "bold" } },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 24;

      doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text("Projected Year-End Ballot", M, y); y += 6;
      doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 8;
      autoTable(doc, {
        startY: y,
        head: [["Line", "Description", "Amount"]],
        body: [
          ["A", "Box 1 wages · gross W-2 income",                                fmt2(ballot.wages)],
          ["B", `2026 standard deduction (${ballot.filingStatus === "mfj" ? "married filing jointly" : "single filer"})`, `-${fmt2(ballot.stdDeduction)}`],
          ["C", "Taxable ordinary income (A − B)",                               fmt2(ballot.taxable)],
          ["D", `Projected federal ordinary tax · marginal ${Math.round(ballot.marginalRate * 100)}%`, fmt2(ballot.tax)],
          ["E", "Box 2 federal income tax already withheld",                     fmt2(ballot.withheld)],
          [
            "F",
            ballot.isRefund ? "Projected Ballot: Estimated Refund Coming Back"
                            : "Projected Ballot: Estimated Liability Due",
            (ballot.isRefund ? "+" : "-") + fmt2(Math.abs(ballot.delta)),
          ],
        ],
        theme: "grid",
        headStyles: { fillColor: [6, 182, 212], textColor: 15, fontStyle: "bold" },
        columnStyles: { 2: { halign: "right", fontStyle: "bold" } },
        margin: { left: M, right: M },
        didParseCell: (data) => {
          if (data.section === "body" && data.row.index === 5) {
            data.cell.styles.fillColor = ballot.isRefund ? [220, 252, 231] : [254, 226, 226];
            data.cell.styles.textColor = ballot.isRefund ? [4, 120, 87] : [153, 27, 27];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 24;

      if (y > H - 220) { doc.addPage(); y = 60; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text("Wealth-Shield Deduction Blueprint", M, y); y += 6;
      doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 18;
      autoTable(doc, {
        startY: y,
        head: [["Shield Vehicle", "2026 Ceiling", "Sweep to Zero Liability", "Est. Tax Cut"]],
        body: [
          ["HSA self-only (IRC §223)", fmt2(HSA_SELF_2026), fmt2(shield.hsaShield), fmt2(Math.round(shield.hsaShield * ballot.marginalRate * 100) / 100)],
          ["Solo 401(k) elective (IRC §402(g))", fmt2(SOLO_401K_2026), fmt2(shield.soloShield), fmt2(Math.round(shield.soloShield * ballot.marginalRate * 100) / 100)],
          ["Combined shield stack", fmt2(SOLO_401K_2026 + HSA_SELF_2026), fmt2(shield.totalShield), fmt2(shield.projectedTaxCut)],
        ],
        theme: "grid",
        headStyles: { fillColor: [16, 185, 129], textColor: 15, fontStyle: "bold" },
        columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right", fontStyle: "bold" } },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 14;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(60);
      const blueprintLines = doc.splitTextToSize(shield.paragraph, W - M * 2);
      if (y + blueprintLines.length * 12 > H - 60) { doc.addPage(); y = 60; }
      doc.text(blueprintLines, M, y);
      doc.setTextColor(0);

      // ============================ PAGE 2 · IRS FORM 1040 (Pages 1 & 2) ============================
      doc.addPage();
      _renderFacsimileHeader(doc, W, M, "Form 1040 · U.S. Individual Income Tax Return", "Page 2 of packet · statutory facsimile · pre-populated for CPA review");

      let py = 100;
      doc.setFont("helvetica", "bold"); doc.setFontSize(10);
      doc.text("Taxpayer Identification", M, py); py += 4;
      doc.setDrawColor(180); doc.line(M, py, W - M, py); py += 10;
      const identRows = [
        ["Full Legal Name",              taxpayer.full_name    || "____________________________________"],
        ["Social Security Number (SSN)", taxpayer.ssn          || "___-__-____"],
        ["Physical Mailing Address",     taxpayer.address_line || "____________________________________"],
        ["City · State · ZIP",           taxpayer.city_state_zip || "____________________________________"],
        ["Filing Status",                ballot.filingStatus === "mfj" ? "☒ Married Filing Jointly" : "☒ Single (or head-of-household)"],
      ];
      identRows.forEach(([k, v]) => {
        doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text(k + ":", M, py);
        doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.text(String(v), M + 170, py);
        py += 16;
      });

      // Digital Asset declaration
      py += 6;
      doc.setFont("helvetica", "bold"); doc.setFontSize(10);
      doc.text("Digital Asset Declaration (Required · IRS Notice 2014-21)", M, py); py += 4;
      doc.setDrawColor(180); doc.line(M, py, W - M, py); py += 12;
      const digitalYes = taxpayer.digital_asset_yn === "yes";
      const digitalNo  = taxpayer.digital_asset_yn === "no";
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      const q = "At any time during the tax year, did you: (a) receive (as a reward, award, or payment for property or services); or (b) sell, exchange, or otherwise dispose of a digital asset (or a financial interest in a digital asset)?";
      const qLines = doc.splitTextToSize(q, W - M * 2 - 60);
      doc.text(qLines, M, py); py += qLines.length * 12 + 4;
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text(digitalYes ? "☒" : "☐", M, py); doc.text("Yes", M + 16, py);
      doc.text(digitalNo  ? "☒" : "☐", M + 80, py); doc.text("No",  M + 96, py);
      py += 22;

      // Income lines (Form 1040 page 1 body)
      doc.setFont("helvetica", "bold"); doc.setFontSize(10);
      doc.text("Income · Form 1040 Page 1", M, py); py += 4;
      doc.setDrawColor(180); doc.line(M, py, W - M, py); py += 8;
      autoTable(doc, {
        startY: py,
        head: [["Line", "Description", "Amount"]],
        body: [
          ["1a",  "Total W-2 box 1 wages",                                    fmt2(ballot.wages)],
          ["1z",  "Total wages (sum of lines 1a–1h)",                          fmt2(ballot.wages)],
          ["3b",  "Ordinary dividends (attach Sch. B if required)",            "$0.00"],
          ["8",   "Additional income from Schedule 1, line 10",                fmt2(scheduleCNet)],
          ["9",   "Total income · sum lines 1z, 2b, 3b, 4b, 5b, 6b, 7, and 8", fmt2(ballot.wages + scheduleCNet)],
          ["10",  "Adjustments to income from Schedule 1, line 26",            "$0.00"],
          ["11",  "Adjusted gross income (line 9 − line 10)",                  fmt2(ballot.wages + scheduleCNet)],
          ["12",  `Standard deduction (${ballot.filingStatus === "mfj" ? "MFJ" : "Single"})`, fmt2(ballot.stdDeduction)],
          ["14",  "Sum of lines 12 and 13 (QBI deduction)",                    fmt2(ballot.stdDeduction)],
          ["15",  "Taxable income (line 11 − line 14, not less than zero)",   fmt2(Math.max(0, ballot.wages + scheduleCNet - ballot.stdDeduction))],
        ],
        theme: "grid",
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 44, halign: "center" }, 2: { halign: "right", fontStyle: "bold" } },
        margin: { left: M, right: M },
      });

      // ============================ PAGE 3 · Form 1040 page 2 ============================
      doc.addPage();
      _renderFacsimileHeader(doc, W, M, "Form 1040 · Page 2 · Tax & Payments", `Page 3 of packet · statutory facsimile · Filing status: ${ballot.filingStatus === "mfj" ? "Married Filing Jointly" : "Single"}`);
      py = 100;
      autoTable(doc, {
        startY: py,
        head: [["Line", "Description", "Amount"]],
        body: [
          ["16",  `Tax (from tax tables · ${Math.round(ballot.marginalRate*100)}% marginal bracket)`, fmt2(ballot.tax)],
          ["17",  "Amount from Schedule 2, line 3",                             "$0.00"],
          ["18",  "Add lines 16 and 17",                                        fmt2(ballot.tax)],
          ["19",  "Child tax credit / credit for other dependents",             "$0.00"],
          ["22",  "Subtract line 21 from line 18 (not less than zero)",         fmt2(ballot.tax)],
          ["23",  "Other taxes · Schedule 2, line 21 · Self-Employment Tax",    fmt2(cashFlashSE)],
          ["24",  "Total tax (add lines 22 and 23)",                            fmt2(totalTaxWithSE)],
          ["25a", "Federal income tax withheld from W-2 (Box 2)",               fmt2(ballot.withheld)],
          ["25c", "Other forms (Cash Flash quarterly reservations)",            "$0.00"],
          ["33",  "Total payments (sum of 25a–25c, 26, 27, 31)",                fmt2(ballot.withheld)],
          ["34",  `Overpayment (line 33 > line 24). ${deltaWithSE >= 0 ? "REFUND" : "—"}`, deltaWithSE >= 0 ? fmt2(deltaWithSE) : "$0.00"],
          ["37",  `Amount you owe (line 24 > line 33). ${deltaWithSE < 0 ? "DUE" : "—"}`,   deltaWithSE < 0 ? fmt2(Math.abs(deltaWithSE)) : "$0.00"],
        ],
        theme: "grid",
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 44, halign: "center" }, 2: { halign: "right", fontStyle: "bold" } },
        margin: { left: M, right: M },
        didParseCell: (data) => {
          if (data.section === "body" && data.row.index === 10 && deltaWithSE >= 0) {
            data.cell.styles.fillColor = [220, 252, 231]; data.cell.styles.textColor = [4, 120, 87];
          }
          if (data.section === "body" && data.row.index === 11 && deltaWithSE < 0) {
            data.cell.styles.fillColor = [254, 226, 226]; data.cell.styles.textColor = [153, 27, 27];
          }
        },
      });
      py = doc.lastAutoTable.finalY + 26;

      // Perjury signature block
      if (py > H - 200) { doc.addPage(); py = 60; }
      doc.setFillColor(248, 250, 252); doc.rect(M, py, W - M * 2, 130, "F");
      doc.setDrawColor(120); doc.rect(M, py, W - M * 2, 130);
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(15, 23, 42);
      doc.text("Sign Here · Under Penalties of Perjury", M + 10, py + 18);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(30, 41, 59);
      const perjury = doc.splitTextToSize(
        "Under penalties of perjury, I declare that I have examined this return and accompanying schedules and statements, and to the best of my knowledge and belief, they are true, correct, and complete. Declaration of preparer (other than taxpayer) is based on all information of which preparer has any knowledge.",
        W - M * 2 - 20,
      );
      doc.text(perjury, M + 10, py + 34);
      doc.setDrawColor(60);
      doc.line(M + 10,       py + 100, M + 250, py + 100); // signature
      doc.line(M + 270,      py + 100, M + 420, py + 100); // date
      doc.line(M + 440,      py + 100, W - M - 10, py + 100); // occupation
      // Stamp compressed canvas signature if the taxpayer executed one.
      if (signatureDataUrl) {
        try { doc.addImage(signatureDataUrl, "PNG", M + 12, py + 60, 230, 40); } catch { /* ignore */ }
        doc.setFontSize(7); doc.setTextColor(120);
        doc.text(now.toLocaleDateString(), M + 280, py + 94);
        doc.setTextColor(0);
      }
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5);
      doc.text("Your signature",   M + 10,  py + 114);
      doc.text("Date",              M + 270, py + 114);
      doc.text("Your occupation",   M + 440, py + 114);
      doc.setTextColor(0);

      // ============================ PAGE 4 · Schedule C ============================
      doc.addPage();
      _renderFacsimileHeader(doc, W, M, "Schedule C · Profit or Loss From Business (Sole Proprietorship)", `Page 4 of packet · statutory facsimile · ${cashFlashCount} Cash Flash entries aggregated`);
      py = 100;
      doc.setFont("helvetica", "bold"); doc.setFontSize(10);
      doc.text("Part I · Income", M, py); py += 4;
      doc.setDrawColor(180); doc.line(M, py, W - M, py); py += 8;
      autoTable(doc, {
        startY: py,
        head: [["Line", "Description", "Amount"]],
        body: [
          ["1", `Gross receipts or sales (Cash Flash Inbound Ledger · ${cashFlashCount} entries)`, fmt2(cashFlashGross)],
          ["2", "Returns and allowances",                              "$0.00"],
          ["3", "Subtract line 2 from line 1",                          fmt2(cashFlashGross)],
          ["4", "Cost of goods sold (from line 42)",                    "$0.00"],
          ["5", "Gross profit (subtract line 4 from line 3)",           fmt2(cashFlashGross)],
          ["6", "Other income · federal/state gasoline tax credit",     "$0.00"],
          ["7", "Gross income · add lines 5 and 6",                     fmt2(cashFlashGross)],
        ],
        theme: "grid",
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 44, halign: "center" }, 2: { halign: "right", fontStyle: "bold" } },
        margin: { left: M, right: M },
      });
      py = doc.lastAutoTable.finalY + 18;
      doc.setFont("helvetica", "bold"); doc.setFontSize(10);
      doc.text("Part II · Expenses (attach receipts from your Receipt Sheets vault)", M, py); py += 4;
      doc.setDrawColor(180); doc.line(M, py, W - M, py); py += 8;
      autoTable(doc, {
        startY: py,
        head: [["Line", "Description", "Amount"]],
        body: [
          ["8",  "Advertising",                     "$0.00"],
          ["9",  `Car and truck expenses (Auto Vault · ${autoCount} entries × business-use%)`, fmt2(autoLine9)],
          ["13", `Depreciation and §179 expense (Capital Vault · ${capitalCount} assets, Yr-1)`,  fmt2(capitalLine13)],
          ["16b","Interest · other",                "$0.00"],
          ["18", "Office expense",                  "$0.00"],
          ["22", "Supplies",                        "$0.00"],
          ["25", `Utilities · home office (Home Vault · ${homeCount} bills)`,                fmt2(homeLine25)],
          ["28", "Total expenses (auto-pulled)",                                              fmt2(scheduleCExpenses)],
          ["31", "Net profit or loss (line 7 − line 28)",                                     fmt2(scheduleCNet)],
        ],
        theme: "grid",
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 44, halign: "center" }, 2: { halign: "right", fontStyle: "bold" } },
        margin: { left: M, right: M },
      });

      // ============================ Safe-Handoff Disclosures ============================
      doc.addPage();
      _renderFacsimileHeader(doc, W, M, "Path A · Safe Handoff Disclosures", "Final page · CPA hand-off manifest");
      py = 100;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(60);
      const notes = [
        "Receipt Sheets is a software provider, not a certified public accounting firm.",
        "No direct IRS re-transmission — hand this packet to a CPA, TurboTax, or TaxSlayer.",
        "Amended returns and international structures are routed through a Help-Desk ticket.",
        "Form 1040 and Schedule C pages are pre-hydrated facsimiles for CPA review only, not official IRS-filed originals.",
        "Ballpark refund/liability figures use 2026 single-filer/MFJ standard deductions and bracket estimates; state, credits, and other adjustments are excluded.",
        "SSNs and taxpayer identification are stored ONLY in your browser localStorage — Receipt Sheets servers never receive them.",
      ];
      notes.forEach((n) => { doc.text(`• ${n}`, M, py); py += 14; });
      doc.setTextColor(0);

      const fname = `irs_1040_schedule_c_packet_${now.toISOString().slice(0, 10)}.pdf`;
      doc.save(fname);
      toast.success(
        deltaWithSE >= 0 ? `Packet exported · Refund ${fmt2(Math.abs(deltaWithSE))}` : `Packet exported · Liability ${fmt2(Math.abs(deltaWithSE))}`,
        { description: `${cashFlashCount} Cash Flash sales · Accuracy ${accuracy.pct.toFixed(1)}% · ${fname}` },
      );
    } catch {
      toast.error("Packet export failed");
    }
  };

// Facsimile-header helper — matches the visual signal of the IRS forms.
function _renderFacsimileHeader(doc, W, M, title, subtitle) {
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, W, 78, "F");
  doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(15);
  doc.text(title, M, 40);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(147, 197, 253);
  doc.text(subtitle, M, 60);
  doc.setTextColor(0);
}


  const downloadScheduleC = async () => {
    if (schedCLoading) return;
    setSchedCLoading(true);
    try {
      const currentYear = new Date().getFullYear();
      const taxYear = currentYear - 1;
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/tax-docs/download-schedule-c?year=${taxYear}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("session_token") || ""}`,
          },
        }
      );
      if (!resp.ok) {
        const errJson = await resp.json().catch(() => ({}));
        throw new Error(errJson.detail || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `receiptsheets-schedule-c-pre-fill-${taxYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Schedule C Pre-Fill PDF downloaded · Tax Year ${taxYear}`);
    } catch (e) {
      toast.error(e?.message || "Schedule C download failed");
    } finally {
      setSchedCLoading(false);
    }
  };


  return (
    <div
      data-testid="w2-ingest-portal"
      className="relative rounded-2xl p-5 mb-5 w2-portal-halo"
      style={{
        background: "linear-gradient(180deg, rgba(6,25,55,0.85), rgba(30,15,60,0.85))",
        border: "1px solid rgba(6,182,212,0.4)",
      }}
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-cyan-300 flex items-center gap-1.5">
          <Lock size={12} /> Year-End Tax Documents · W-2 Chroma Vault
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={downloadScheduleC}
            disabled={schedCLoading}
            data-testid="w2-download-schedule-c-btn"
            className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:text-white hover:bg-emerald-500/25 transition-colors disabled:opacity-60 disabled:cursor-wait"
            title="Download server-side ReportLab Schedule C IRS Pre-Fill PDF"
          >
            {schedCLoading ? (
              <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : (
              <Download size={11} />
            )}
            {schedCLoading ? "Generating…" : "Schedule C PDF"}
          </button>
          <button
            type="button"
            onClick={() => setResetOpen(true)}
            data-testid="w2-reset-form-btn"
            className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:text-white hover:bg-rose-500/25 transition-colors"
            title="Wipe every W-2 field, taxpayer profile, signature, and backend vault row"
          >
            <RotateCcw size={11} /> Clear & Reset Form
          </button>
          <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-md border border-purple-500/40 bg-purple-500/15 text-purple-100">
            Path A · Safe Handoff Builder
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        data-testid="w2-ingest-open-btn"
        className={`w-full h-14 rounded-xl text-white font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 transition-transform ${busy ? "opacity-70 cursor-wait" : "hover:-translate-y-px"}`}
        style={{
          background: "linear-gradient(135deg, #06b6d4, #7c3aed)",
          boxShadow: "0 12px 28px -12px rgba(124,58,237,0.6)",
        }}
      >
        <Lock size={14} /> {busy ? "Chroma-scanning…" : "🔒 Ingest Year-End Tax Documents"}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="w2-ingest-file-input"
        onChange={(e) => onFile(e.target.files?.[0])}
      />

      {/* Document preview + white-hot laser sweep overlay */}
      {preview && (
        <div
          className="mt-4 relative overflow-hidden rounded-xl border border-purple-500/30 bg-slate-950/70"
          data-testid="w2-preview-container"
        >
          <img
            src={preview}
            alt="Uploaded W-2"
            className="w-full max-h-[320px] object-contain"
            data-testid="w2-preview-img"
          />
          {sweeping && (
            <div
              data-testid="w2-laser-sweep"
              className="absolute inset-x-0 h-1 pointer-events-none"
              style={{
                top: 0,
                background: "linear-gradient(90deg, transparent, #fff, transparent)",
                boxShadow: "0 0 24px 6px rgba(255,255,255,0.85)",
                animation: "w2-price-flare-sweep 1.5s cubic-bezier(.22,1,.36,1) forwards",
              }}
            />
          )}
        </div>
      )}

      {/* Extracted metrics + Form 1040 packet trigger */}
      {w2 && (
        <div
          data-testid="w2-success-panel"
          className="mt-4 rounded-xl p-5 border w2-success-frame"
          style={{ background: "rgba(15,23,42,0.6)" }}
        >
          <div className="flex items-center gap-2 text-emerald-300 text-[11px] font-mono uppercase tracking-widest mb-4">
            <ShieldCheck size={12} /> Extraction complete · encrypted vault stored
          </div>

          {/* Employer / metadata block */}
          <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldRow testid="w2-field-employer"     label="Employer (Box c)" value={w2.employer || "—"} tone="cyan" />
            <FieldRow testid="w2-field-ein"          label="EIN · Box b"      value={w2.employer_ein || "—"} tone="cyan" mono />
            <FieldRow testid="w2-field-tax-year"     label="Tax Year"         value={w2.tax_year != null ? String(w2.tax_year) : "—"} tone="cyan" mono />
          </div>

          {/* Federal wages block */}
          <div className="mb-3 text-[10px] font-mono uppercase tracking-[0.24em] text-purple-300">Federal · Form 1040</div>
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldRow
              testid="w2-field-gross-wages"
              label="Box 1 · Wages, tips, other compensation"
              value={fmt(w2.gross_wages ?? w2.wages_tips)}
              tone="violet"
              highlight
            />
            <FieldRow
              testid="w2-field-federal-withheld"
              label="Box 2 · Federal income tax withheld"
              value={fmt(w2.federal_withheld)}
              tone="violet"
              highlight
            />
          </div>

          {/* State wages block */}
          <div className="mb-3 text-[10px] font-mono uppercase tracking-[0.24em] text-emerald-300">
            State · {w2.state_code || w2.state || "—"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldRow
              testid="w2-field-state-code"
              label="Box 15 · State"
              value={w2.state_code || w2.state || "—"}
              tone="emerald"
              mono
            />
            <FieldRow
              testid="w2-field-state-wages"
              label="Box 16 · State wages"
              value={fmt(w2.state_wages)}
              tone="emerald"
            />
            <FieldRow
              testid="w2-field-state-withheld"
              label="Box 17 · State tax withheld"
              value={fmt(w2.state_withheld)}
              tone="emerald"
            />
          </div>

          <div
            className="mt-5 rounded-lg border border-cyan-500/25 bg-slate-950/50"
            data-testid="w2-taxpayer-panel"
          >
            <button
              type="button"
              onClick={() => setTaxpayerOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-cyan-200/80"
              data-testid="w2-taxpayer-toggle"
            >
              <span>Taxpayer Identification · SSN stays on device</span>
              <span>{taxpayerOpen ? "▲" : "▼"}</span>
            </button>
            {taxpayerOpen && (
              <div className="p-3 border-t border-cyan-500/15 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <input
                  data-testid="w2-taxpayer-name"
                  placeholder="Full Legal Name"
                  value={taxpayer.full_name || ""}
                  onChange={(e) => patchTaxpayer("full_name", e.target.value)}
                  className="h-9 px-3 rounded-md bg-slate-950/70 border border-cyan-500/25 focus:border-cyan-400/70 text-white font-mono placeholder:text-slate-500 focus:outline-none"
                />
                <input
                  data-testid="w2-taxpayer-ssn"
                  placeholder="SSN · 123-45-6789"
                  value={taxpayer.ssn || ""}
                  onChange={(e) => patchTaxpayer("ssn", e.target.value)}
                  className="h-9 px-3 rounded-md bg-slate-950/70 border border-cyan-500/25 focus:border-cyan-400/70 text-white font-mono placeholder:text-slate-500 focus:outline-none"
                />
                <input
                  data-testid="w2-taxpayer-address"
                  placeholder="Mailing Address"
                  value={taxpayer.address_line || ""}
                  onChange={(e) => patchTaxpayer("address_line", e.target.value)}
                  className="h-9 px-3 rounded-md bg-slate-950/70 border border-cyan-500/25 focus:border-cyan-400/70 text-white font-mono placeholder:text-slate-500 focus:outline-none md:col-span-2"
                />
                <input
                  data-testid="w2-taxpayer-city"
                  placeholder="City · State · ZIP"
                  value={taxpayer.city_state_zip || ""}
                  onChange={(e) => patchTaxpayer("city_state_zip", e.target.value)}
                  className="h-9 px-3 rounded-md bg-slate-950/70 border border-cyan-500/25 focus:border-cyan-400/70 text-white font-mono placeholder:text-slate-500 focus:outline-none md:col-span-2"
                />
                <div className="md:col-span-2 flex items-center gap-3 px-1 pt-1">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-cyan-200/80">
                    IRS Digital Asset Question:
                  </span>
                  {["yes", "no"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => patchTaxpayer("digital_asset_yn", v)}
                      data-testid={`w2-digital-asset-${v}`}
                      className={`px-3 h-7 rounded-md text-[10px] font-mono uppercase tracking-widest border transition-colors ${
                        taxpayer.digital_asset_yn === v
                          ? "bg-cyan-500/25 border-cyan-400/70 text-cyan-100"
                          : "border-cyan-500/25 text-slate-400 hover:text-cyan-200"
                      }`}
                    >
                      {v === "yes" ? "☒ Yes" : "☒ No"}
                    </button>
                  ))}
                </div>

                <div className="md:col-span-2 pt-2" data-testid="w2-signature-pad-wrap">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-cyan-200/80">
                      ✍️ Execute Legal Filing Signature
                    </span>
                    <button
                      type="button"
                      data-testid="w2-signature-clear"
                      onClick={() => {
                        const c = sigCanvasRef.current;
                        if (c) { const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height); }
                        setSignatureDataUrl(null);
                      }}
                      className="text-[9px] font-mono uppercase tracking-widest text-slate-400 hover:text-cyan-200"
                    >
                      Clear
                    </button>
                  </div>
                  <canvas
                    ref={sigCanvasRef}
                    data-testid="w2-signature-canvas"
                    width={540}
                    height={110}
                    className="w-full rounded-md bg-slate-950/80 border border-cyan-500/30 cursor-crosshair"
                    style={{ touchAction: "none" }}
                    onPointerDown={(e) => {
                      const c = sigCanvasRef.current;
                      const r = c.getBoundingClientRect();
                      const scaleX = c.width / r.width;
                      const scaleY = c.height / r.height;
                      sigDrawing.current = true;
                      sigLast.current = { x: (e.clientX - r.left) * scaleX, y: (e.clientY - r.top) * scaleY };
                      const ctx = c.getContext("2d");
                      ctx.strokeStyle = "#67e8f9";
                      ctx.lineWidth = 2;
                      ctx.lineCap = "round";
                      ctx.lineJoin = "round";
                      c.setPointerCapture(e.pointerId);
                    }}
                    onPointerMove={(e) => {
                      if (!sigDrawing.current) return;
                      const c = sigCanvasRef.current;
                      const r = c.getBoundingClientRect();
                      const scaleX = c.width / r.width;
                      const scaleY = c.height / r.height;
                      const x = (e.clientX - r.left) * scaleX;
                      const y = (e.clientY - r.top) * scaleY;
                      const ctx = c.getContext("2d");
                      ctx.beginPath();
                      ctx.moveTo(sigLast.current.x, sigLast.current.y);
                      ctx.lineTo(x, y);
                      ctx.stroke();
                      sigLast.current = { x, y };
                    }}
                    onPointerUp={() => {
                      if (!sigDrawing.current) return;
                      sigDrawing.current = false;
                      try {
                        const c = sigCanvasRef.current;
                        setSignatureDataUrl(c.toDataURL("image/png"));
                      } catch { /* ignore */ }
                    }}
                    onPointerLeave={() => { sigDrawing.current = false; }}
                  />
                  <div className="text-[9px] font-mono text-slate-500 mt-1">
                    {signatureDataUrl ? "Signature captured · will stamp into perjury block on packet export." : "Sign above with mouse or finger; released stroke saves automatically."}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className="mt-3 flex items-center justify-between rounded-lg p-3 border border-cyan-500/25 bg-slate-950/50"
            data-testid="w2-filing-status-toggle"
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-200/80">
              Filing Status · adjusts 2026 std deduction + brackets
            </div>
            <div className="inline-flex rounded-md overflow-hidden border border-cyan-500/40">
              {["single", "mfj"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFilingStatus(s)}
                  data-testid={`w2-filing-${s}`}
                  className={`px-3 h-8 text-[11px] font-mono uppercase tracking-widest transition-colors ${
                    filingStatus === s
                      ? "bg-cyan-500/30 text-cyan-100"
                      : "bg-transparent text-slate-400 hover:text-cyan-200"
                  }`}
                  style={filingStatus === s ? { textShadow: "0 0 8px rgba(6,182,212,0.55)" } : undefined}
                >
                  {s === "single" ? "Single" : "MFJ"}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={generatePacket}
            data-testid="w2-generate-packet-btn"
            className="mt-3 w-full h-11 rounded-lg text-slate-950 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:-translate-y-px transition-transform"
            style={{
              background: "linear-gradient(135deg, #fde047, #10b981)",
              boxShadow: "0 10px 24px -10px rgba(16,185,129,0.55)",
            }}
          >
            <FileText size={13} /> ⚡ Generate and Download IRS Form 1040 + Schedule C Packet
          </button>
        </div>
      )}

      <style>{`
        @keyframes w2-portal-breathe {
          0%, 100% { box-shadow: 0 0 0 1px rgba(6,182,212,0.45), 0 0 22px rgba(6,182,212,0.35), 0 0 30px rgba(124,58,237,0.25); }
          50%      { box-shadow: 0 0 0 2px rgba(124,58,237,0.55), 0 0 40px rgba(124,58,237,0.65), 0 0 60px rgba(6,182,212,0.55); }
        }
        .w2-portal-halo { animation: w2-portal-breathe 4s ease-in-out infinite; }
        @keyframes w2-price-flare-sweep {
          0%   { top: 0%;   opacity: 0.9; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes w2-success-glow {
          0%, 100% { border-color: rgba(124,58,237,0.5); box-shadow: 0 0 22px rgba(124,58,237,0.35); }
          50%      { border-color: rgba(16,185,129,0.7); box-shadow: 0 0 32px rgba(16,185,129,0.55); }
        }
        .w2-success-frame { animation: w2-success-glow 3s ease-in-out infinite; border-color: rgba(124,58,237,0.5); }
        @media (prefers-reduced-motion: reduce) {
          .w2-portal-halo, .w2-success-frame { animation: none !important; }
        }
      `}</style>
      <ConfirmResetDialog
        open={resetOpen}
        title="Reset Tax Packet?"
        body="⚠️ Are you sure you want to permanently clear this data ledger? This will flush every extracted W-2 field, your taxpayer profile, signature, filing status, and every persisted W-2 row from your encrypted vault. This action cannot be undone."
        confirmLabel="♻️ Wipe Tax Packet"
        busy={resetBusy}
        onCancel={() => setResetOpen(false)}
        onConfirm={performTaxPacketReset}
        testid="w2-reset"
      />
    </div>
  );
}

function FieldRow({ label, value, testid, tone = "cyan", mono = false, highlight = false }) {
  const palette = {
    cyan:    { border: "rgba(103,232,249,0.35)", bg: "rgba(6,182,212,0.08)",  label: "text-cyan-300",    val: "text-cyan-100"    },
    violet:  { border: "rgba(168,85,247,0.4)",   bg: "rgba(124,58,237,0.10)", label: "text-purple-300",  val: "text-purple-100"  },
    emerald: { border: "rgba(52,211,153,0.4)",   bg: "rgba(16,185,129,0.08)", label: "text-emerald-300", val: "text-emerald-100" },
  }[tone] || {};
  return (
    <div
      data-testid={testid}
      className="rounded-lg p-3 border"
      style={{
        borderColor: palette.border,
        background: palette.bg,
        boxShadow: highlight ? `0 0 18px ${palette.border}` : "none",
      }}
    >
      <div className={`text-[9px] font-mono uppercase tracking-widest ${palette.label}`}>{label}</div>
      <div className={`mt-1 text-lg font-bold ${palette.val} ${mono ? "font-mono" : ""} ${highlight ? "text-xl" : ""}`}>
        {value}
      </div>
    </div>
  );
}

export default W2IngestPortal;
